import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/app/providers/TenantProvider';
import { TenantsList } from '@/features/superadmin/components/TenantsList';
import {
  Shield,
  Building2,
  Activity,
  CheckCircle2,
  XCircle,
  Clock,
  Database,
  Sparkles,
  MessageSquare,
  Loader2,
  Save,
} from 'lucide-react';


interface TenantSummary {
  id: string;
  slug: string;
  plan: string;
  active: boolean;
  created_at: string;
  tenant_branding: { app_name: string; candidate_name: string | null; party: string | null } | null;
  member_count?: number;
}

interface AuditEntry {
  id: string;
  action: string;
  resource: string;
  created_at: string;
  tenant_id: string | null;
  user_id: string | null;
}

export function SuperadminDashboard() {
  const { switchTenant } = useTenant();
  const [activeTab, setActiveTab] = useState<'tenants' | 'planos' | 'logs'>('tenants');

  // Lista de todos os tenants
  const { data: tenants = [], isLoading: tenantsLoading } = useQuery<TenantSummary[]>({
    queryKey: ['superadmin', 'all-tenants'],
    queryFn: async (): Promise<TenantSummary[]> => {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, slug, plan, active, created_at, tenant_branding(app_name, candidate_name, party)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      if (!data) return [];
      return (data as Record<string, unknown>[]).map((row) => ({
        id: row['id'] as string,
        slug: row['slug'] as string,
        plan: row['plan'] as string,
        active: row['active'] as boolean,
        created_at: row['created_at'] as string,
        tenant_branding: row['tenant_branding'] as TenantSummary['tenant_branding'],
      }));
    },
    staleTime: 30_000,
  });

  // Últimos 50 eventos de auditoria globais
  const { data: auditLogs = [], isLoading: logsLoading } = useQuery<AuditEntry[]>({
    queryKey: ['superadmin', 'audit-logs-global'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('id, action, resource, created_at, tenant_id, user_id')
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });

  const totalActive = tenants.filter(t => t.active).length;
  const totalInactive = tenants.filter(t => !t.active).length;

  return (
    <div className="space-y-8 animate-fade-in pb-12">
      {/* Header Premium */}
      <div
        className="force-dark relative rounded-3xl overflow-hidden p-8 border shadow-2xl"
        style={{
          background: 'linear-gradient(135deg, #0A1628 0%, #0d2446 50%, #091e40 100%)',
          borderColor: 'rgba(0,194,255,0.2)',
        }}
      >
        {/* Efeitos de luz */}
        <div className="absolute top-[-40%] right-[-5%] w-[35%] h-[150%] rounded-full blur-[80px] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,194,255,0.12), transparent)' }} />
        <div className="absolute bottom-[-50%] left-[-5%] w-[25%] h-[120%] rounded-full blur-[60px] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,90,255,0.08), transparent)' }} />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest border"
              style={{ background: 'rgba(0,194,255,0.1)', borderColor: 'rgba(0,194,255,0.35)', color: '#00C2FF' }}>
              <Shield className="h-3.5 w-3.5" />
              Painel de Superadmin
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold text-ink tracking-tight">
              Controle Global da Plataforma
            </h1>
            <p className="text-sm text-slate-400 max-w-xl leading-relaxed">
              Acesso irrestrito a todos os tenants, configurações e logs de auditoria da plataforma Lumenise Base.
            </p>
          </div>

          {/* Métricas resumidas no header */}
          <div className="flex gap-4 flex-wrap">
            <div className="flex flex-col items-center px-5 py-3 rounded-2xl border"
              style={{ background: 'rgba(0,194,255,0.07)', borderColor: 'rgba(0,194,255,0.2)' }}>
              <span className="text-2xl font-extrabold text-ink">{tenants.length}</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">Tenants</span>
            </div>
            <div className="flex flex-col items-center px-5 py-3 rounded-2xl border"
              style={{ background: 'rgba(16,185,129,0.07)', borderColor: 'rgba(16,185,129,0.2)' }}>
              <span className="text-2xl font-extrabold text-emerald-400">{totalActive}</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">Ativos</span>
            </div>
            <div className="flex flex-col items-center px-5 py-3 rounded-2xl border"
              style={{ background: 'rgba(239,68,68,0.07)', borderColor: 'rgba(239,68,68,0.2)' }}>
              <span className="text-2xl font-extrabold text-red-400">{totalInactive}</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">Inativos</span>
            </div>
            <div className="flex flex-col items-center px-5 py-3 rounded-2xl border"
              style={{ background: 'rgba(168,85,247,0.07)', borderColor: 'rgba(168,85,247,0.2)' }}>
              <span className="text-2xl font-extrabold text-purple-400">{auditLogs.length}</span>
              <span className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">Logs Recentes</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-midnight-3 border border-white/8 w-fit">
        {([ ['tenants', 'Tenants', Building2], ['planos', 'Planos & Limites', MessageSquare], ['logs', 'Logs de Auditoria', Activity]] as const).map(([tab, label, Icon]) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
              activeTab === tab
                ? 'bg-accent text-[#0A2342] shadow-md'
                : 'text-ink-muted hover:text-accent'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab: Tenants — CRUD completo */}
      {activeTab === 'tenants' && (
        <TenantsList onView={(id) => switchTenant(id)} />
      )}

      {/* Tab: Planos & Limites */}
      {activeTab === 'planos' && (
        <PlanLimitsAdmin tenants={tenants} tenantsLoading={tenantsLoading} />
      )}

      {/* Tab: Logs de Auditoria */}
      {activeTab === 'logs' && (
        <div className="rounded-2xl bg-midnight-3 border border-white/8 overflow-hidden">
          <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
            <h2 className="text-sm font-bold text-ink flex items-center gap-2">
              <Database className="h-4 w-4 text-accent" />
              Trilha de Auditoria Global
            </h2>
            <span className="text-xs text-ink-muted">{auditLogs.length} eventos mais recentes</span>
          </div>

          {logsLoading ? (
            <LoadingSkeleton rows={8} />
          ) : auditLogs.length === 0 ? (
            <EmptyState
              icon={Activity}
              title="Nenhum evento registrado"
              description="O log de auditoria está vazio. Ações relevantes serão registradas aqui automaticamente."
            />
          ) : (
            <div className="divide-y divide-border/40">
              {auditLogs.map((log) => (
                <div key={log.id} className="flex items-center gap-4 px-6 py-4 hover:bg-midnight-2/50 transition-colors">
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(0,194,255,0.1)', color: '#00C2FF' }}>
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div className="flex-grow min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-ink font-mono">{log.action}</span>
                      <span className="text-[10px] text-ink-muted">em</span>
                      <span className="text-xs font-semibold text-accent font-mono">{log.resource}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      {log.tenant_id && (
                        <span className="text-[10px] text-slate-500 font-mono">
                          tenant: {log.tenant_id.slice(0, 8)}…
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-[10px] text-slate-500 whitespace-nowrap shrink-0 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {new Date(log.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Painel: Planos & Limites ──────────────────────────────────────────────────

const PLAN_OPTIONS = ['starter', 'growth', 'pro', 'enterprise'] as const;
type PlanName = typeof PLAN_OPTIONS[number];

const PLAN_DEFAULTS: Record<PlanName, Partial<PlanLimitFormValues>> = {
  starter:    { plan_name: 'starter',    daily_message_limit: 250,   monthly_message_limit: 5000,   max_campaign_size: 250,  max_campaigns_per_day: 2, min_cooldown_minutes: 1440, allowed_categories: 'lideranca,equipe' },
  growth:     { plan_name: 'growth',     daily_message_limit: 1000,  monthly_message_limit: 20000,  max_campaign_size: 500,  max_campaigns_per_day: 4, min_cooldown_minutes: 720,  allowed_categories: 'lideranca,equipe,apoiador' },
  pro:        { plan_name: 'pro',        daily_message_limit: 5000,  monthly_message_limit: 80000,  max_campaign_size: 1000, max_campaigns_per_day: 8, min_cooldown_minutes: 360,  allowed_categories: 'lideranca,equipe,apoiador,militante' },
  enterprise: { plan_name: 'enterprise', daily_message_limit: 20000, monthly_message_limit: 300000, max_campaign_size: 5000, max_campaigns_per_day: 20, min_cooldown_minutes: 60,  allowed_categories: 'lideranca,equipe,apoiador,militante,eleitor' },
};

const planLimitSchema = z.object({
  plan_name:              z.enum(PLAN_OPTIONS),
  daily_message_limit:    z.number().int().min(1).max(100000),
  monthly_message_limit:  z.number().int().min(1).max(1000000),
  max_campaign_size:      z.number().int().min(1).max(10000),
  max_campaigns_per_day:  z.number().int().min(1).max(50),
  allowed_categories:     z.string().min(1),  // csv: "lideranca,equipe"
  min_cooldown_minutes:   z.number().int().min(0).max(10080),
  reputation_floor:       z.number().min(0).max(1),
});

type PlanLimitFormValues = z.infer<typeof planLimitSchema>;

interface PlanLimitsAdminProps {
  tenants: TenantSummary[];
  tenantsLoading: boolean;
}

function PlanLimitsAdmin({ tenants, tenantsLoading }: PlanLimitsAdminProps) {
  const qc = useQueryClient();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  // Carrega os limites do tenant selecionado
  const { data: limits, isLoading: limitsLoading } = useQuery({
    queryKey: ['superadmin', 'plan-limits', selectedTenantId],
    queryFn: async () => {
      if (!selectedTenantId) return null;
      const { data } = await (supabase as any)
        .from('tenant_plan_limits')
        .select('*')
        .eq('tenant_id', selectedTenantId)
        .maybeSingle();
      return data as (PlanLimitFormValues & { allowed_categories: string[] }) | null;
    },
    enabled: !!selectedTenantId,
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<PlanLimitFormValues>({
    resolver: zodResolver(planLimitSchema),
    defaultValues: PLAN_DEFAULTS.starter,
  });

  // Preenche o form quando os dados chegam
  React.useEffect(() => {
    if (limits) {
      reset({
        plan_name:             limits.plan_name as PlanName,
        daily_message_limit:   limits.daily_message_limit,
        monthly_message_limit: limits.monthly_message_limit,
        max_campaign_size:     limits.max_campaign_size,
        max_campaigns_per_day: limits.max_campaigns_per_day,
        allowed_categories:    Array.isArray(limits.allowed_categories)
          ? limits.allowed_categories.join(',')
          : limits.allowed_categories,
        min_cooldown_minutes:  limits.min_cooldown_minutes,
        reputation_floor:      limits.reputation_floor ?? 0.95,
      });
    }
  }, [limits, reset]);

  const saveMutation = useMutation({
    mutationFn: async (values: PlanLimitFormValues) => {
      if (!selectedTenantId) return;
      const payload = {
        ...values,
        tenant_id:          selectedTenantId,
        allowed_categories: values.allowed_categories.split(',').map((s) => s.trim()).filter(Boolean),
      };
      const { error } = await (supabase as any)
        .from('tenant_plan_limits')
        .upsert(payload, { onConflict: 'tenant_id' });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['superadmin', 'plan-limits', selectedTenantId] });
      qc.invalidateQueries({ queryKey: ['tenant-plan-limits'] });
    },
  });

  const watchedPlan = watch('plan_name');

  const applyPreset = (plan: PlanName) => {
    const preset = PLAN_DEFAULTS[plan];
    Object.entries(preset).forEach(([key, val]) => {
      setValue(key as keyof PlanLimitFormValues, val as never, { shouldDirty: true });
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Lista de tenants */}
      <div className="lg:col-span-1 rounded-2xl bg-midnight-3 border border-white/8 overflow-hidden">
        <div className="px-4 py-3 border-b border-white/8">
          <h3 className="text-sm font-bold text-ink">Tenants</h3>
        </div>
        {tenantsLoading ? (
          <div className="p-4"><LoadingSkeleton rows={4} /></div>
        ) : (
          <div className="divide-y divide-white/5 max-h-[480px] overflow-y-auto">
            {tenants.map((t) => {
              const label = t.tenant_branding?.app_name || t.slug;
              const isSelected = t.id === selectedTenantId;
              return (
                <button
                  key={t.id}
                  onClick={() => setSelectedTenantId(t.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors ${
                    isSelected ? 'bg-accent/10 border-l-2 border-accent' : ''
                  }`}
                >
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center text-xs font-black shrink-0"
                    style={{ background: 'rgba(0,194,255,0.12)', color: '#00C2FF' }}>
                    {label[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className={`text-xs font-semibold truncate ${isSelected ? 'text-accent' : 'text-ink'}`}>{label}</p>
                    <p className="text-[10px] text-ink-muted font-mono">{t.plan}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Editor de limites */}
      <div className="lg:col-span-2 rounded-2xl bg-midnight-3 border border-white/8 overflow-hidden">
        <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
          <h3 className="text-sm font-bold text-ink">
            {selectedTenantId
              ? `Limites: ${tenants.find((t) => t.id === selectedTenantId)?.tenant_branding?.app_name ?? '...'}`
              : 'Selecione um tenant'}
          </h3>
          {limits && (
            <span className="text-[10px] font-semibold px-2 py-1 rounded border" style={{ background: 'rgba(0,194,255,0.1)', borderColor: 'rgba(0,194,255,0.3)', color: '#00C2FF' }}>
              {limits.plan_name?.toUpperCase()}
            </span>
          )}
        </div>

        {!selectedTenantId ? (
          <EmptyState
            icon={MessageSquare}
            title="Nenhum tenant selecionado"
            description="Escolha um tenant na lista ao lado para editar seus limites de mensagens."
          />
        ) : limitsLoading ? (
          <div className="p-6"><LoadingSkeleton rows={6} /></div>
        ) : (
          <>
          <form
            onSubmit={handleSubmit((v) => saveMutation.mutateAsync(v))}
            className="p-6 flex flex-col gap-5"
          >
            {/* Presets rápidos */}
            <div>
              <p className="text-xs font-semibold text-ink-muted mb-2 uppercase tracking-wider">Aplicar preset</p>
              <div className="flex gap-2 flex-wrap">
                {PLAN_OPTIONS.map((plan) => (
                  <button
                    key={plan}
                    type="button"
                    onClick={() => applyPreset(plan)}
                    className={`text-xs px-3 py-1.5 rounded-lg border font-semibold transition-all capitalize ${
                      watchedPlan === plan
                        ? 'bg-accent/20 border-accent/40 text-accent'
                        : 'bg-white/5 border-white/10 text-ink-muted hover:bg-white/10'
                    }`}
                  >
                    {plan}
                  </button>
                ))}
              </div>
            </div>

            {/* Grid de campos */}
            <div className="grid grid-cols-2 gap-4">
              <AdminFormField label="Msgs/dia" error={errors.daily_message_limit?.message}>
                <input type="number" min={1} max={100000} className="input-base w-full text-sm"
                  {...register('daily_message_limit', { valueAsNumber: true })} />
              </AdminFormField>
              <AdminFormField label="Msgs/mês" error={errors.monthly_message_limit?.message}>
                <input type="number" min={1} max={1000000} className="input-base w-full text-sm"
                  {...register('monthly_message_limit', { valueAsNumber: true })} />
              </AdminFormField>
              <AdminFormField label="Contatos por campanha" error={errors.max_campaign_size?.message}>
                <input type="number" min={1} max={10000} className="input-base w-full text-sm"
                  {...register('max_campaign_size', { valueAsNumber: true })} />
              </AdminFormField>
              <AdminFormField label="Campanhas por dia" error={errors.max_campaigns_per_day?.message}>
                <input type="number" min={1} max={50} className="input-base w-full text-sm"
                  {...register('max_campaigns_per_day', { valueAsNumber: true })} />
              </AdminFormField>
              <AdminFormField label="Cooldown mínimo (min)" error={errors.min_cooldown_minutes?.message}>
                <input type="number" min={0} max={10080} className="input-base w-full text-sm"
                  {...register('min_cooldown_minutes', { valueAsNumber: true })} />
              </AdminFormField>
              <AdminFormField label="Reputação mínima (0–1)" error={errors.reputation_floor?.message}>
                <input type="number" min={0} max={1} step={0.001} className="input-base w-full text-sm"
                  {...register('reputation_floor', { valueAsNumber: true })} />
              </AdminFormField>
              <AdminFormField
                label="Categorias permitidas (csv)"
                hint='Ex: "lideranca,equipe,apoiador"'
                error={errors.allowed_categories?.message}
                className="col-span-2"
              >
                <input type="text" className="input-base w-full text-sm font-mono"
                  placeholder="lideranca,equipe"
                  {...register('allowed_categories')} />
              </AdminFormField>
            </div>

            {/* Feedback */}
            {saveMutation.isError && (
              <p className="text-xs text-rose-400 flex items-center gap-2">
                <XCircle className="h-4 w-4" /> Erro ao salvar. Tente novamente.
              </p>
            )}
            {saveMutation.isSuccess && !isDirty && (
              <p className="text-xs text-emerald-400 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> Limites salvos com sucesso.
              </p>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!isDirty || isSubmitting || saveMutation.isPending}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: '#00C2FF', color: '#0A2342' }}
              >
                {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Salvar limites
              </button>
            </div>
          </form>

          {/* Configuração WhatsApp agora na aba "WhatsApp" do modal de edição do tenant */}
          </>
        )}
      </div>
    </div>
  );
}

function AdminFormField({
  label,
  hint,
  error,
  children,
  className = '',
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <label className="text-xs font-semibold text-ink-muted">{label}</label>
      {hint && <p className="text-[10px] text-ink-subtle -mt-1">{hint}</p>}
      {children}
      {error && <p className="text-[10px] text-rose-400">{error}</p>}
    </div>
  );
}

function LoadingSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-3 p-6">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-10 rounded-lg bg-border/30 animate-pulse" style={{ opacity: 1 - i * 0.1 }} />
      ))}
    </div>
  );
}

function EmptyState({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center space-y-3">
      <div className="h-14 w-14 rounded-2xl flex items-center justify-center"
        style={{ background: 'rgba(0,194,255,0.08)', color: '#00C2FF' }}>
        <Icon className="h-7 w-7" />
      </div>
      <h3 className="text-sm font-bold text-ink">{title}</h3>
      <p className="text-xs text-ink-muted max-w-xs leading-relaxed">{description}</p>
    </div>
  );
}
